import { Card, Space, Tag, Tooltip, Typography } from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExperimentOutlined,
  FilePdfOutlined,
  HomeOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import type { DashboardAuditCard } from "../types";

const { Text } = Typography;

interface Props {
  audit: DashboardAuditCard;
  /** When true, hide the auditor chip (used in panels that already
   *  scope to one auditor — e.g. the auditor's own dashboard). */
  hideAuditor?: boolean;
}

/**
 * Compact audit card shared by every list panel on the dashboard.
 * Click → /audits/{id}. Indicators row reflects the closure-flow
 * artefacts (acta legal, acta de cierre, sample count) so the
 * planner / auditor can tell at a glance what's still pending.
 */
export const AuditCard = ({ audit, hideAuditor = false }: Props) => {
  const navigate = useNavigate();
  const stateName = (audit.audit_state || "").toLowerCase();
  const stateColor = stateName.includes("cancel")
    ? "red"
    : stateName.includes("finaliz")
      ? "green"
      : stateName.includes("planific")
        ? "orange"
        : "default";

  return (
    <Card
      size="small"
      hoverable
      style={{ marginBottom: 8 }}
      onClick={() => navigate(`/audits/${audit.id}`)}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <Space size={6} wrap style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: 13 }}>
              {audit.reference || `#${audit.id}`}
            </Text>
            {audit.audit_type && (
              <Tag color="cyan" style={{ margin: 0 }}>
                {audit.audit_type}
              </Tag>
            )}
            {audit.audit_state && (
              <Tag color={stateColor} style={{ margin: 0 }}>
                {audit.audit_state}
              </Tag>
            )}
          </Space>
          <div
            style={{
              fontSize: 13,
              color: "#262626",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <HomeOutlined style={{ marginRight: 6, color: "#8c8c8c" }} />
            <strong>{audit.farm_name || "—"}</strong>
            {audit.producer_name && (
              <span style={{ color: "#8c8c8c" }}>
                {" "}
                · {audit.producer_name}
              </span>
            )}
          </div>
          <Space size={12} wrap style={{ marginTop: 4, fontSize: 12, color: "#595959" }}>
            {audit.planning_date && (
              <span>
                <CalendarOutlined style={{ marginRight: 4 }} />
                {dayjs(audit.planning_date).format("DD/MM/YYYY")}
              </span>
            )}
            {audit.start_time && (
              <span>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                {audit.start_time.slice(0, 5)}
                {audit.finishing_time
                  ? `–${audit.finishing_time.slice(0, 5)}`
                  : ""}
              </span>
            )}
            {!hideAuditor && audit.auditor_name && (
              <span>
                <UserOutlined style={{ marginRight: 4 }} />
                {audit.auditor_name}
              </span>
            )}
          </Space>
        </div>
        <Space direction="vertical" size={4} align="end">
          <Tooltip title={audit.has_audit_act ? "Acta legal cargada" : "Sin acta legal"}>
            <FilePdfOutlined
              style={{
                color: audit.has_audit_act ? "#52c41a" : "#d9d9d9",
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              audit.has_closure_report
                ? "Acta de cierre generada"
                : "Sin acta de cierre"
            }
          >
            <CheckCircleOutlined
              style={{
                color: audit.has_closure_report ? "#0a8a18" : "#d9d9d9",
              }}
            />
          </Tooltip>
          <Tooltip title={`${audit.samples_count} muestra(s)`}>
            <span style={{ fontSize: 12, color: "#722ed1" }}>
              <ExperimentOutlined style={{ marginRight: 2 }} />
              {audit.samples_count}
            </span>
          </Tooltip>
        </Space>
      </div>
    </Card>
  );
};

export default AuditCard;
